// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TwoMonthDayView } from '@/components/two-month-day-view';
import { MAXIMUM_DATE, MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  clampedDateForArrowKey,
  getSectionType,
} from '@/utilities/date-picker-clamping';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef } from 'react';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

export interface DateShortcut {
  label: string;
  getValue: () => DateTime;
}

function parseValue(value: unknown) {
  if (value instanceof DateTime) {
    return value.isValid ? value : null;
  }

  return null;
}

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, past values are disabled and invalid.
   */
  disablePast?: boolean;

  /**
   * If true, future values are disabled and invalid.
   */
  disableFuture?: boolean;

  /**
   * If provided, the minimum date that can be selected.
   */
  minimumDate?: DateTime;

  /**
   * If provided, the maximum date that can be selected.
   */
  maximumDate?: DateTime;

  /**
   * If true, the input is disabled.
   */
  isDisabled?: boolean;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * A function that determines if a date should be disabled.
   */
  shouldDisableDate?: (dateTime: DateTime) => boolean;

  /**
   * If true, the date can be cleared
   */
  isClearable?: boolean;

  /**
   * An optional array of pre-defined date shortcuts that can be used to quickly
   * select a specific date.
   */
  shortcuts?: DateShortcut[];

  /**
   * Represents the placeholder text for a given input or UI element.
   * This text is typically displayed as a hint to the user about the
   * expected input format or content when the field is empty.
   */
  placeholderText?: string;

  /**
   * Overrides the default field.onChange handler entirely. The caller is
   * responsible for updating the form value. Used by useFieldArray callers
   * that need to replace the entire row on change.
   */
  onChange?: (date: DateTime | null) => void;

  /**
   * Called after the default field.onChange has already updated the form
   * value. Use for side effects like cascading to other fields.
   */
  onChangeComplete?: (date: DateTime | null) => void;

  /**
   * When the value is null, the calendar opens to this date's month
   * instead of the current month. Useful for pairing start/end pickers
   * so the end picker opens to the start date's month.
   */
  referenceDate?: DateTime;

  /**
   * Number of month grids to render in the calendar popper. Defaults to 1.
   * Set to 2 to display the anchor month and the following month side by
   * side, useful when the user often picks dates that span the end of one
   * month into the next (e.g. ship and delivery dates).
   */
  monthsToShow?: 1 | 2;
}

/**
 * A controlled date picker component that integrates with react-hook-form.
 * Wraps the MUI DatePicker with a capture-phase keydown listener that clamps
 * month/year arrow key changes to valid day ranges, preventing Luxon errors
 * from invalid dates like June 31.
 */
export function FormDatePicker<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  const value = parseValue(field.value);
  const format = 'yyyy-MM-dd';

  const containerRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const handleChange = (date: DateTime | null) => {
    if (props.onChange) {
      props.onChange(date);
      return;
    }
    if (date === null) {
      field.onChange(null);
    } else {
      field.onChange(date);
    }
    props.onChangeComplete?.(date);
  };

  const handleChangeRef = useRef(handleChange);
  handleChangeRef.current = handleChange;

  // Capture-phase keydown listener intercepts arrow keys BEFORE MUI's
  // internal handler, preventing Luxon from receiving invalid dates like
  // June 31 when arrowing from July 31.
  const handleArrowKey = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    const currentValue = valueRef.current;
    if (!currentValue?.isValid) return;

    if (!(event.target instanceof HTMLInputElement)) return;
    const input = event.target;

    const pos = input.selectionStart ?? 0;
    const selEnd = input.selectionEnd ?? 0;
    const sectionType = getSectionType(pos, input.value, format);

    if (sectionType !== 'M' && sectionType !== 'y') return;

    const clamped = clampedDateForArrowKey(
      currentValue,
      event.key as 'ArrowUp' | 'ArrowDown',
      sectionType
    );

    event.preventDefault();
    event.stopImmediatePropagation();
    handleChangeRef.current(clamped);

    requestAnimationFrame(() => {
      input.setSelectionRange(pos, selEnd);
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('keydown', handleArrowKey, true);
    return () => el.removeEventListener('keydown', handleArrowKey, true);
  }, [handleArrowKey]);

  // When `monthsToShow === 2`, the day view is rendered by `TwoMonthDayView`,
  // which uses React hooks. Lock the outer picker to the `day` view so the
  // toolbar (e.g. on `MobileDatePicker`) cannot switch to the `year` view and
  // skip `TwoMonthDayView` on the next render — that would violate React's
  // rules of hooks.
  const isTwoMonth = props.monthsToShow === 2;
  const viewRenderers = isTwoMonth ? { day: TwoMonthDayView } : undefined;
  const views = isTwoMonth ? (['day'] as const) : undefined;

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <DatePicker
        format={format}
        orientation={props.shortcuts === undefined ? undefined : 'portrait'}
        disabled={props.isDisabled}
        disablePast={props.disablePast}
        disableFuture={props.disableFuture}
        minDate={props.minimumDate ?? MINIMUM_DATE}
        maxDate={props.maximumDate ?? MAXIMUM_DATE}
        referenceDate={props.referenceDate}
        name={field.name}
        inputRef={field.ref}
        value={value}
        onChange={handleChange}
        shouldDisableDate={props.shouldDisableDate}
        viewRenderers={viewRenderers}
        views={views}
        view={isTwoMonth ? 'day' : undefined}
        slotProps={{
          field: {
            clearable: props.isClearable,
          },
          shortcuts: { items: props.shortcuts },
          textField: {
            size: 'small',
            label: props.label,
            error: Boolean(fieldState.error),
            fullWidth: true,
            helperText: fieldState.error?.message,
            disabled: props.isDisabled,
            placeholder: props.placeholderText,
            autoComplete: 'off',
          },
        }}
      />
    </div>
  );
}
